import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Checkbox, Input, Space, Spin, message, Form } from 'antd';
import {
  updateConfig,
  changeCustomisationModalVisibility,
  verifyConfig,
  clearConfig,
  saveConfig,
} from '@store/serviceIntegrations/integrationForm/actions';
import {
  nameSelector,
  emailsSelector,
  defaultFieldsSelector,
  alertTypeSelector,
  partnerDetailsSelector,
  configIdSelector,
} from '@store/serviceIntegrations/integrationForm/selectors';
import { SERVICE_INTEGRATION_MODE_MAIN, CURRENT_PRODUCT_MODE } from '@utils/constants';
import { setMode } from '@store/serviceIntegrations/base/actions';
import getDefaultFields from './default-fields';
import AiAlertCustomisation from './ai-alert-customisation';
import ManualAlertCustomisation from './manual-alert-customisation';
import VerifyModal from './verify-modal';

import {
  AlertConfigRow,
  ContentWrapper,
  RequiredFieldsNote,
  FormWrapper,
  Separator,
  ButtonWrapper,
  CustomizeAlert,
  BreadCrumb,
  SiText,
  PartnerText,
  ConsentWrapper,
  ConsentPara,
} from './styled';
import useGetConfig from './useGetConfig';

function IntegrationForm() {
  const dispatch = useDispatch();

  const [integrationForm] = Form.useForm();

  const name = useSelector(nameSelector);
  const emails = useSelector(emailsSelector);
  const configId = useSelector(configIdSelector);
  const partnerDetails = useSelector(partnerDetailsSelector);
  const alertType = useSelector(alertTypeSelector);
  const defaultFieldsValues = useSelector(defaultFieldsSelector);

  const [isVerified, setIsVerified] = useState(false);
  const [consentCheck, setConsentCheck] = useState(false);
  const [consentEnabled, setConsentEnabled] = useState(true);
  const defaultFields = getDefaultFields(partnerDetails.partnerName) || [];

  const displayManualAlerts = CURRENT_PRODUCT_MODE !== 'APP';
  const { isLoading } = useGetConfig({ configId });

  useEffect(() => {
    if (configId) {
      setConsentCheck(true);
      setConsentEnabled(false);
    }
  }, []);

  if (isLoading) {
    return (
      <Space>
        <Spin />
      </Space>
    );
  }

  const onValuesChange = () => {
    setIsVerified(false);
  };

  const formatValues = (values) => {
    const vendorFields: any = [];
    const { name, emails } = values;

    let alertType = '';
    const { manualAlert, aiAlert } = values;
    if (manualAlert && aiAlert) {
      alertType = 'ALL';
    } else if (manualAlert) {
      alertType = 'MA';
    } else if (aiAlert) {
      alertType = 'AI';
    }

    defaultFields.forEach((field) => {
      if (field.name in values) {
        vendorFields.push({ name: field.name, value: values[field.name] });
      }
    });

    return { name, emails, alertType, defaultFields: vendorFields };
  };

  const onConsentCheckboxChange = (e) => {
    setConsentCheck(e.target.checked);
  };

  const showCustomModal = (type) => {
    dispatch(changeCustomisationModalVisibility(type));
  };

  const onVerify = () => {
    integrationForm
      .validateFields()
      .then((values) => {
        dispatch(updateConfig(formatValues(values)));
        dispatch(verifyConfig());
      })
      .catch((errors) => {
        message.error('There are validation errors on the form.');
      });
  };

  const onSave = () => {
    dispatch(saveConfig());
  };

  const goToMainView = () => {
    dispatch(clearConfig());
    dispatch(setMode(SERVICE_INTEGRATION_MODE_MAIN));
  };

  return (
    <ContentWrapper>
      <BreadCrumb>
        <SiText onClick={goToMainView}>Service Integrations</SiText>
        <PartnerText>{` / ${partnerDetails.partnerName}`}</PartnerText>
      </BreadCrumb>

      <RequiredFieldsNote>Fields are mandatory</RequiredFieldsNote>

      <FormWrapper>
        <Form layout="vertical" form={integrationForm} onValuesChange={onValuesChange}>
          <Form.Item
            label="Name"
            name="name"
            tooltip={{
              title:
                'Enter the name of this service. This name will appear in emails about the service.',
              placement: 'topLeft',
            }}
            rules={[{ required: true, message: 'Please enter a name' }]}
            initialValue={name}
          >
            <Input />
          </Form.Item>

          {defaultFields.map((configItem) => (
            <Form.Item
              key={configItem.name}
              label={configItem.label}
              name={configItem.name}
              tooltip={{ title: configItem.helpText, placement: 'topLeft' }}
              rules={[
                {
                  required: configItem.required,
                  message: configItem.errorText,
                },
              ]}
              initialValue={
                (defaultFieldsValues.find((item) => item.name === configItem.name) || { value: '' })
                  .value
              }
            >
              <Input type={configItem.type} />
            </Form.Item>
          ))}

          <Form.Item
            label="Email"
            name="emails"
            tooltip={{
              title:
                'Specify the email addresses that will receive alert emails. Use a comma to separate multiple emails.',
              placement: 'topLeft',
            }}
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const emailArray = value.split(',').map((email) => email.trim());
                  const re =
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                  return emailArray.every((email) => re.test(email)) && emailArray.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter valid emails separated by commas.'));
                },
              },
            ]}
            initialValue={emails}
          >
            <Input />
          </Form.Item>

          <AlertConfigRow>
            <Form.Item
              label="AI Alerts"
              tooltip={{
                title: 'Set the severity and custom payloads for AI Alerts.',
                placement: 'topLeft',
              }}
              initialValue={alertType === 'AI' || alertType === 'ALL'}
              name="aiAlert"
              valuePropName="checked"
              rules={[{ required: false }]}
            >
              <Checkbox />
            </Form.Item>
            {/* tslint:disable-next-line jsx-no-lambda */}
            <CustomizeAlert style={{ marginLeft: -50 }} onClick={() => showCustomModal('ai')}>
              Customize AI Alerts
            </CustomizeAlert>
          </AlertConfigRow>

          {displayManualAlerts && (
            <AlertConfigRow>
              <Form.Item
                label="Manual Alerts"
                tooltip={{
                  title: 'Set the priority and custom payloads for Manual Alerts.',
                  placement: 'topLeft',
                }}
                name="manualAlert"
                valuePropName="checked"
                initialValue={alertType === 'MA' || alertType === 'ALL'}
                rules={[{ required: false }]}
              >
                <Checkbox />
              </Form.Item>
              {/* tslint:disable-next-line jsx-no-lambda */}
              <CustomizeAlert style={{ marginLeft: -85 }} onClick={() => showCustomModal('manual')}>
                Customize Manual Alerts
              </CustomizeAlert>
            </AlertConfigRow>
          )}
        </Form>

        <ConsentWrapper>
          <Checkbox
            onChange={onConsentCheckboxChange}
            checked={consentCheck}
            disabled={!consentEnabled}
          />
          <ConsentPara>
            By clicking here, you authorize Conviva to share Conviva alert data with the person or
            entity to whom this
            {` ${partnerDetails.partnerName} `}
            integration is directed, and you acknowledge and agree that Conviva shall have no
            responsibility or liability for such person’s or entity’s use of this data.
          </ConsentPara>
        </ConsentWrapper>
        <Separator />

        <ButtonWrapper>
          <Button onClick={goToMainView}>Cancel</Button>
          {!isVerified ? (
            <Button type="primary" onClick={onVerify} disabled={!consentCheck}>
              Verify
            </Button>
          ) : (
            <Button type="primary" onClick={onSave} disabled={!consentCheck}>
              Save
            </Button>
          )}
        </ButtonWrapper>
      </FormWrapper>
      {/* tslint:disable-next-line jsx-no-lambda */}
      <VerifyModal partnerDetails={partnerDetails} onVerify={() => setIsVerified(true)} />
      <AiAlertCustomisation />
      <ManualAlertCustomisation />
    </ContentWrapper>
  );
}

export default IntegrationForm;
